import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Link as LinkIcon, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { storageHelpers } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';

interface ImageUploaderProps {
  bucketName: string;
  label: string;
  value: string;
  onChange: (url: string) => void;
  options?: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    format?: string;
  };
  placeholder?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  bucketName,
  label,
  value,
  onChange,
  options = { maxWidth: 1200, maxHeight: 1200, quality: 0.85, format: 'webp' },
  placeholder = 'URL da imagem ou upload...'
}) => {
  const { showToast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showUrlFallback, setShowUrlFallback] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processUpload = async (file: File) => {
    setUploading(true);
    setErrorMessage(null);
    try {
      const result = await storageHelpers.uploadGenericFile(bucketName, file, options);
      if (result.error) {
        if (result.error === 'ARMAZENAMENTO_CHEIO') {
          const errMsg = 'Armazenamento do Supabase Cheio! Por favor, remova arquivos antigos ou mude seu plano. Use o link manual abaixo se necessário.';
          setErrorMessage(errMsg);
          showToast({
            type: 'error',
            title: 'Limite Excedido',
            message: 'A cota de armazenamento gratuito do Supabase está cheia.'
          });
          setShowUrlFallback(true);
        } else {
          setErrorMessage(`Falha no upload: ${result.error}`);
          showToast({
            type: 'error',
            title: 'Erro de Upload',
            message: result.error
          });
        }
      } else if (result.url) {
        onChange(result.url);
        showToast({
          type: 'success',
          title: 'Imagem Enviada!',
          message: 'Imagem otimizada e salva com sucesso.'
        });
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Erro inesperado no upload');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processUpload(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processUpload(e.dataTransfer.files[0]);
    }
  };

  const removeImage = () => {
    onChange('');
    setErrorMessage(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {label}
        </label>
        <button
          type="button"
          onClick={() => setShowUrlFallback(!showUrlFallback)}
          className="text-xs text-[#0FFF55] hover:underline flex items-center space-x-1"
        >
          <LinkIcon size={12} />
          <span>{showUrlFallback ? 'Ocultar Link Manual' : 'Inserir Link Manual'}</span>
        </button>
      </div>

      {/* Upload Drop Zone / Preview */}
      {!value ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center min-h-[120px] text-center ${
            isDragging
              ? 'border-[#0FFF55] bg-[#0FFF55]/5'
              : 'border-gray-800 hover:border-[#0FFF55]/40 bg-[#16191d]'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center space-y-2">
              <RefreshCw size={24} className="text-[#0FFF55] animate-spin" />
              <p className="text-xs text-gray-400 font-medium">Otimizando e enviando imagem...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <div className="p-2.5 bg-gray-900 border border-gray-800 rounded-lg text-gray-400 group-hover:text-[#0FFF55]">
                <Upload size={18} />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-200">
                  Arraste a imagem aqui ou clique para selecionar
                </p>
                <p className="text-[10px] text-gray-500 mt-1">
                  Formatos aceitos: PNG, JPG, WebP. Compressão WebP automática.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Image Preview Box */
        <div className="relative border border-gray-800 bg-[#16191d] rounded-xl overflow-hidden p-2 flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="w-14 h-14 bg-[#121316] rounded-lg overflow-hidden border border-gray-850 flex-shrink-0">
              <img
                src={value}
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop';
                }}
              />
            </div>
            <div className="overflow-hidden">
              <span className="text-[10px] text-[#0FFF55] font-bold flex items-center gap-1 mb-0.5">
                <CheckCircle2 size={12} />
                <span>IMAGEM CARREGADA</span>
              </span>
              <p className="text-[10px] text-gray-400 truncate max-w-[250px]" title={value}>
                {value}
              </p>
            </div>
          </div>
          
          <button
            type="button"
            onClick={removeImage}
            className="p-1.5 bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white rounded-lg transition-colors border border-red-600/20"
            title="Remover Imagem"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Error alert messages */}
      {errorMessage && (
        <div className="flex items-start space-x-2 p-3 bg-red-600/10 border border-red-600/20 rounded-xl text-[11px] text-red-400">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <p className="leading-normal">{errorMessage}</p>
        </div>
      )}

      {/* URL Link Fallback Input */}
      {showUrlFallback && (
        <div className="pt-1.5">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-4 py-2.5 bg-[#16191d] border border-gray-800 focus:border-[#0FFF55] rounded-xl text-white text-xs outline-none transition-colors placeholder:text-gray-650"
          />
          <p className="text-[9px] text-gray-550 mt-1">
            Se preferir ou se o upload falhar, insira o link direto de uma imagem hospedada.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
